import { Injectable } from '@angular/core';
import { catchError, map, Observable } from 'rxjs';
import { of } from 'rxjs/internal/observable/of';
import { AppContext } from '../data/app-context';
import { Logging } from '../diagnostics/logging';
import { RpcObservableCopilotClient } from '../rpc/copilot/rpc-observable-copilot-client';
import { RpcObservableCopilotConfigServer } from '../rpc/copilot/rpc-observable-copilot-config-server';
import { RpcObservableCopilotError, RpcObservableCopilotRequest, RpcObservableCopilotRequestType, RpcObservableCopilotResult } from '../rpc/copilot/rpc-observable-copilot-model';

@Injectable({
    providedIn: 'root'
})
export class CopilotExtensionService {
    /**
     * The RpcObservableCopilotConfigServer class for sending.
     */
    public rpcObservableCopilotClient: RpcObservableCopilotClient;

    /**
     * The RpcObservableCopilotConfigServer classf for receiving.
     */
    public rpcObservableCopilotConfigServer: RpcObservableCopilotConfigServer;

    /**
     * Request handlers for different types of requests.
     */
    private requestHandlers = {
        [RpcObservableCopilotRequestType.RouteNavigation]: () => this.handleRouteNavigation(),
        [RpcObservableCopilotRequestType.ThemeChange]: (request?: RpcObservableCopilotRequest) => this.handleThemeChange(request)
    };

    private readonly serviceName = 'CopilotExtensionService';

    private copilotResultMessage = {
        success: 'Data sent to shell successfully.',
        error: 'Error occurred while sending data to shell.',
        notInitialized: 'The rpcObservableCopilotClient is not initialized.',
        rpcNotProvided: 'The rpc object is not provided.',
        handlerNotFound: 'No handler found for request type: '
    };

    constructor(protected appContext: AppContext) {
        if (!appContext?.rpc) {
            Logging.logError(this.serviceName, this.copilotResultMessage.rpcNotProvided);
            return;
        }

        this.rpcObservableCopilotClient = new RpcObservableCopilotClient(appContext.rpc);
        this.rpcObservableCopilotConfigServer = new RpcObservableCopilotConfigServer(appContext.rpc);
    }

    /**
     * Register custom handlers for different types of requests.
     * @param customHandlers Custom handlers for different types of requests.
     */
    public registerRpcHandler(customHandlers = {}) {
        const handlers = { ...this.requestHandlers, ...customHandlers };

        this.rpcObservableCopilotConfigServer.register(request => {
            const handler = handlers[request.type];

            if (!handler) {
                Logging.logError(this.serviceName, `${this.copilotResultMessage.handlerNotFound}${request.type}`);
                return of(null);
            }

            return handler(request);
        });
    }

    /**
     * Send to shell by rpc and then request shell to send prompt from extension to copilot.
     * @param prompt the prompt to send to copilot
     *
     */
    public sendToShellSendingPromptToCopilot(prompt: string): Observable<RpcObservableCopilotResult | RpcObservableCopilotError> {
        const config = {
            type: RpcObservableCopilotRequestType.RequestToSendToShellSendingPromptToCopilot,
            copilotChatRequest: {
                prompt
            }
        };
        return this.sendToShellByRpc(config);
    }

    /**
     * Send to shell by rpc to request sending copilot response to extension.
     * @param response the response to send to extension.
     * @param targetExtension the target extension to send the response.
     */
    public sendToShellSendingCopilotResponseToExtension(response: string, targetExtension: string): Observable<RpcObservableCopilotResult | RpcObservableCopilotError> {
        const config = {
            type: RpcObservableCopilotRequestType.RequestToSendToExtensionSendingCopilotResponse,
            copilotChatResponse: {
                response,
                targetExtension
            }
        };

        return this.sendToShellByRpc(config);
    }

    /**
     * Send to shell by rpc to request for theme update.
     */
     public sendToShellUpdateTheme(): Observable<RpcObservableCopilotResult | RpcObservableCopilotError> {
        const config = {
            type: RpcObservableCopilotRequestType.RequestThemeChange,
            themeChange: {
                current: MsftSme.self().Resources.theme === 'dark' ? 'light' : 'dark',
                previous: MsftSme.self().Resources.theme
            }
        };

        return this.sendToShellByRpc(config);
    }

    /**
     * Send to shell by rpc to request url
     */
    public sendToShellRequestUrl(): Observable<RpcObservableCopilotResult | RpcObservableCopilotError> {
        const config = {
            type: RpcObservableCopilotRequestType.RequestRouteNavigation
        };

        return this.sendToShellByRpc(config);
    }

    /**
     * Send to shell by rpc to request tool
     */
    public sendToShellRequestTool(): Observable<RpcObservableCopilotResult | RpcObservableCopilotError> {
        const config = {
            type: RpcObservableCopilotRequestType.RequestToolChange
        };

        return this.sendToShellByRpc(config);
    }

    /**
     * Send to shell by rpc to request active node.
     */
    public sendToShellRequestActiveNodeName(): Observable<RpcObservableCopilotResult | RpcObservableCopilotError> {
        const config = {
            type: RpcObservableCopilotRequestType.RequestActiveNodeName
        };

        return this.sendToShellByRpc(config);
    }

    /**
     * Send an RPC request to the specified target with the provided configuration.
     *
     * @param config The configuration object to send with the RPC request.
     */
    private sendToShellByRpc(config: any): Observable<RpcObservableCopilotResult | RpcObservableCopilotError> {
        if (!this.rpcObservableCopilotClient) {
            return of(<RpcObservableCopilotError>{
                error: new Error(this.copilotResultMessage.notInitialized),
                message: this.copilotResultMessage.notInitialized
            });
        }

        return this.rpcObservableCopilotClient.log(config).pipe(
            map(result => {
                return <RpcObservableCopilotResult>{
                    ...result,
                    message: this.copilotResultMessage.success
                };
            }),
            catchError((error) => {
                return of(<RpcObservableCopilotError>{
                    error: error,
                    message: this.copilotResultMessage.error
                });
            })
        );
    }

    /**
     * Default handler for navigation request.
     */
    private handleRouteNavigation(): Observable<void> {
        return of(null);
    }

    /**
     * Default handler for theme update request.
     */
    private handleThemeChange(request?: RpcObservableCopilotRequest): Observable<void> {
        const theme = request?.themeChange?.current;
        if (theme && MsftSme.self().Resources.theme !== theme) {
            MsftSme.self().Resources.theme = theme;
            document.body.classList.remove(`sme-theme-${theme === 'dark' ? 'light' : 'dark'}`);
            document.body.classList.add(`sme-theme-${theme}`);
        }
        return of(null);
    }
}
